import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import searchService from "./searchService";

/**
 * Query keys for search operations
 */
export const searchKeys = {
  all: ["search"],
  global: (query) => ["search", "global", query],
  communities: (query) => ["search", "communities", query],
  threads: (query) => ["search", "threads", query],
};

/**
 * Custom hook for global search with debouncing
 * Searches both communities and threads in parallel
 * 
 * @param {string} query - Search query from user input
 * @param {Object} options - Hook options
 * @param {number} options.debounceMs - Debounce delay in ms (default: 300)
 * @param {boolean} options.enabled - Whether search is enabled (default: true)
 * @returns {Object} Search results and state
 */
export const useGlobalSearch = (query, options = {}) => {
  const { debounceMs = 300, enabled = true } = options;

  // Debounce the query
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Determine if search should run
  const shouldSearch = useMemo(
    () => enabled && debouncedQuery && debouncedQuery.trim().length >= 2,
    [enabled, debouncedQuery]
  );

  // Main search query
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: searchKeys.global(debouncedQuery),
    queryFn: async () => {
      const result = await searchService.globalSearch(debouncedQuery);
      return result.data;
    },
    enabled: shouldSearch,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Computed values
  const communities = data?.communities || [];
  const threads = data?.threads || [];
  const topics = data?.topics || [];
  const totalResults = data?.totalResults || 0;
  const hasResults = totalResults > 0;
  const isSearching = isFetching && shouldSearch;
  const threadsAreFiltered = data?.threadsAreFiltered || false;

  return {
    // Data
    communities,
    threads,
    topics,
    totalResults,
    hasResults,
    searchQuery: debouncedQuery,
    threadsAreFiltered, // Indicates threads are tag-filtered, not searched

    // State
    isLoading: isLoading && shouldSearch,
    isSearching,
    isError,
    error,

    // Actions
    refetch,

    // Computed
    shouldSearch,
    isQueryTooShort: query.trim().length > 0 && query.trim().length < 2,
  };
};

/**
 * Hook for searching communities only
 * @param {string} query - Search query
 * @param {number} limit - Max results
 * @returns {Object} Community search results
 */
export const useSearchCommunities = (query, limit = 20) => {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const shouldSearch = debouncedQuery && debouncedQuery.trim().length >= 2;

  return useQuery({
    queryKey: searchKeys.communities(debouncedQuery),
    queryFn: async () => {
      // Use new API signature with options object
      const result = await searchService.searchCommunities({ 
        query: debouncedQuery, 
        limit 
      });
      return result.data;
    },
    enabled: shouldSearch,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for global thread search
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Object} Thread search results
 */
export const useSearchThreads = (query, options = {}) => {
  const { limit = 20, sortBy = "relevance", tags = [] } = options;
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const shouldSearch = debouncedQuery && debouncedQuery.trim().length >= 2;

  return useQuery({
    queryKey: [...searchKeys.threads(debouncedQuery), sortBy, tags],
    queryFn: async () => {
      const result = await searchService.searchThreads({ 
        query: debouncedQuery, 
        limit,
        sortBy,
        tags,
      });
      return result.data;
    },
    enabled: shouldSearch,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export default useGlobalSearch;

